import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Upload, 
  FileText, 
  Globe, 
  Plus, 
  Loader,
  CheckCircle,
  Search,
  ArrowRight,
  ArrowLeft,
  Tag,
  Eye,
  X,
  Check,
  XCircle,
  Clock
} from 'lucide-react';
import { testCasesApi, testTypesApi, testTechniquesApi, techniquesApi, documentsApi, contentApi, categoriesApi, CategoryRecommendation } from '../services/api';
import { useSetting } from '../hooks/useSettings';

interface TestCaseGenerationProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

interface DocumentAnalysis {
  summary: string;
  reasoning: string;
  applicable_test_types: Array<{
    id: number;
    name: string;
    purpose?: string;
    prompt?: string;
  }>;
  document_id?: number; // Added for Phase 1 processing
  statistics?: {
    sections: number;
    sentences: number;
    entities: number;
    tables: number;
    references: number;
    topics: number;
  };
  processing_time_ms?: number;
}

interface GeneratedTestCase {
  question: string;
  test_type: string;
  test_type_id?: number;
  test_technique: string;
  test_technique_id?: number;
}

const buildGeneratedLabel = (testCase: Pick<GeneratedTestCase, 'test_type' | 'test_technique'>): string => {
  const labels = [testCase.test_type, testCase.test_technique]
    .map(label => label?.trim())
    .filter((label): label is string => Boolean(label));

  return labels.length > 0 ? labels.join(' / ') : 'Generated';
};

// Helper function to extract reasoning for a specific test type
const extractReasoningForTestType = (reasoning: string, testTypeName: string): string => {
  if (!reasoning) return '';
  
  // Look for patterns like "1. **Test Type Name**: reasoning text"
  const patterns = [
    new RegExp(`\\d+\\.\\s*\\*\\*${testTypeName}\\*\\*[\\s:]+([^\\n]+(?:\\n(?!\\d+\\.|$)[^\\n]*)*)`, 'i'),
    new RegExp(`\\*\\*${testTypeName}\\*\\*[\\s:]+([^\\n]+(?:\\n(?!\\d+\\.|\\*\\*)[^\\n]*)*)`, 'i'),
    new RegExp(`${testTypeName}[\\s:]+([^\\n]+(?:\\n(?!\\d+\\.|\\*\\*)[^\\n]*)*)`, 'i')
  ];
  
  for (const pattern of patterns) {
    const match = reasoning.match(pattern);
    if (match && match[1]) {
      return match[1].trim();
    }
  }
  
  return '';
};

const TestCaseGeneration: React.FC<TestCaseGenerationProps> = ({ addNotification }) => {
  // Track component mount/unmount to detect remounting
  useEffect(() => {
    console.log('🟢 TestCaseGeneration component MOUNTED');
    return () => {
      console.log('🔴 TestCaseGeneration component UNMOUNTED');
    };
  }, []);

  // Track addNotification prop changes
  useEffect(() => {
    console.log('🔄 addNotification prop reference changed');
  }, [addNotification]);

  const [activeTab, setActiveTab] = useState<'pdf' | 'webpage' | 'manual'>('pdf');
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<'upload' | 'selection' | 'generation'>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentAnalysis, setDocumentAnalysis] = useState<DocumentAnalysis | null>(null);
  const [selectedTestTypes, setSelectedTestTypes] = useState<number[]>([]);
  const [allTestTypes, setAllTestTypes] = useState<Array<{id: number, name: string, category: string}>>([]);
  const [recommendedCategories, setRecommendedCategories] = useState<CategoryRecommendation[]>([]);
  
  // Use organization-specific settings
  const maxTestCasesPerGeneration = useSetting<number>('general.max_test_cases_per_generation', 50);
  const [numTestCases, setNumTestCases] = useState(Math.min(5, maxTestCasesPerGeneration));
  const [generatedTestCases, setGeneratedTestCases] = useState<GeneratedTestCase[]>([]);
  const [testCaseStatuses, setTestCaseStatuses] = useState<Record<number, 'APPROVED' | 'REJECTED' | 'PENDING'>>({});
  const [approvingTestCases, setApprovingTestCases] = useState<Set<number>>(new Set());
  const [techniquesUsed, setTechniquesUsed] = useState<Array<{
    id: number;
    name: string;
    test_type: string;
    test_type_id: number;
  }>>([]);
  const [availableTechniques, setAvailableTechniques] = useState<Array<{
    id: number;
    name: string;
    description?: string;
    test_type_id: number;
    test_type_name: string;
    test_type_category: string;
  }>>([]);
  const [selectedTechniqueIds, setSelectedTechniqueIds] = useState<number[]>([]);
  const [loadingTechniques, setLoadingTechniques] = useState(false);
  const [selectedTechniqueForDetails, setSelectedTechniqueForDetails] = useState<{
    id: number;
    name: string;
    description?: string;
    test_type_id: number;
    test_type_name: string;
    test_type_category: string;
  } | null>(null);
  const [techniqueSearchTerm, setTechniqueSearchTerm] = useState('');
  const [techniqueFilterType, setTechniqueFilterType] = useState<'all' | string>('all');
  const [resultGroupBy, setResultGroupBy] = useState<'none' | 'technique' | 'test_type'>('none');
  const [resultFilterTechnique, setResultFilterTechnique] = useState<'all' | string>('all');
  const [resultFilterTestType, setResultFilterTestType] = useState<'all' | string>('all');
  const [resultFilterStatus, setResultFilterStatus] = useState<'all' | 'APPROVED' | 'REJECTED' | 'PENDING'>('all');

  // Use ref to track if analysis is in progress or completed to prevent step resets
  const analysisInProgressRef = useRef(false);
  const analysisCompletedRef = useRef(false);

  // Protected setter for currentStep that prevents resets after analysis
  const setCurrentStepProtected = useCallback((newStep: 'upload' | 'selection' | 'generation', source?: string) => {
    if (newStep === 'upload' && (analysisInProgressRef.current || analysisCompletedRef.current)) {
      console.warn(`⚠️ BLOCKED: Attempt to reset step to 'upload' after analysis (source: ${source || 'unknown'})`);
      console.trace('Stack trace:');
      return; // Block the reset
    }
    console.log(`✅ Setting step to: ${newStep} (source: ${source || 'unknown'})`);
    setCurrentStep(newStep);
  }, []);

  // Debug: Track documentAnalysis changes
  useEffect(() => {
    console.log('🔍 documentAnalysis state changed:', !!documentAnalysis, documentAnalysis ? 'has data' : 'null');
    if (documentAnalysis) {
      console.log('📊 documentAnalysis details:', {
        document_id: documentAnalysis.document_id,
        hasStatistics: !!documentAnalysis.statistics,
        currentStep: currentStep
      });
    }
  }, [documentAnalysis, currentStep]);

  // Debug: Log currentStep changes and track what's changing it
  useEffect(() => {
    console.log('🔍 currentStep changed to:', currentStep);
    // Log stack trace to see what's calling setCurrentStep
    if (currentStep === 'upload' && (analysisInProgressRef.current || analysisCompletedRef.current)) {
      console.warn('⚠️ Step reset to upload after analysis started/completed!', new Error().stack);
    }
  }, [currentStep]);

  const handleTabChange = (tab: 'pdf' | 'webpage' | 'manual') => {
    setActiveTab(tab);
    // Reset analysis flags when changing tabs
    analysisInProgressRef.current = false;
    analysisCompletedRef.current = false;
    setCurrentStep('upload');
    setSelectedFile(null);
    setDocumentAnalysis(null);
    setSelectedTestTypes([]);
    setGeneratedTestCases([]);
    setTechniquesUsed([]);
    setAvailableTechniques([]);
  };

  const handleFileSelect = (file: File) => {
    console.log('📁 handleFileSelect called, currentStep:', currentStep, 'documentAnalysis:', !!documentAnalysis, 'analysisInProgress:', analysisInProgressRef.current, 'analysisCompleted:', analysisCompletedRef.current);
    
    // BLOCK if analysis has completed - don't allow new file selection
    if (analysisCompletedRef.current) {
      console.error('🚫 BLOCKED: Cannot select new file after analysis completed');
      return;
    }
    
    setSelectedFile(file);
    // Only reset to upload step if we haven't started/completed analysis yet
    // Prevent reset if analysis is in progress or completed
    if (!documentAnalysis && !analysisInProgressRef.current && !analysisCompletedRef.current && currentStep !== 'selection' && currentStep !== 'generation') {
      console.log('🔄 Resetting step to upload (new file selected)');
      setCurrentStep('upload'); // Stay on upload step until analysis is complete
    } else {
      console.log('⚠️ Skipping step reset - analysis in progress/completed or already past upload step');
    }
  };

  const handleAnalyzeDocument = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!selectedFile) return;

    try {
      setLoading(true);
      analysisInProgressRef.current = true;
      analysisCompletedRef.current = false;
      
      // Get organization and project IDs from localStorage
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const organizationId = orgIdStr ? parseInt(orgIdStr) : 1;
      const projectId = projectIdStr ? parseInt(projectIdStr) : 1;
      
      // Use NEW Phase 1 DocumentProcessor (NO LLM calls)
      const result = await documentsApi.analyzeDocument(selectedFile, organizationId, projectId, false);
      
      // Store document_id and statistics for display
      const docAnalysis = {
        summary: `Document processed in ${result.processing_time_ms}ms`,
        reasoning: `Document analyzed: ${result.statistics.sections} sections, ${result.statistics.sentences} sentences, ${result.statistics.entities} entities, ${result.statistics.tables} tables, ${result.statistics.topics} topics`,
        applicable_test_types: [], // Phase 1 doesn't return test types (that's for Phase 2)
        document_id: result.document_id,
        statistics: result.statistics, // Add statistics for display
        processing_time_ms: result.processing_time_ms
      };
      console.log('📝 Setting documentAnalysis state:', docAnalysis);
      console.log('🔍 Current state BEFORE setDocumentAnalysis:', {
        documentAnalysis: !!documentAnalysis,
        currentStep,
        selectedFile: !!selectedFile
      });
      setDocumentAnalysis(docAnalysis);
      console.log('✅ documentAnalysis state set, component should re-render');
      
      // Use a small delay to check if state persists after notification
      setTimeout(() => {
        console.log('🔍 State check AFTER setDocumentAnalysis (100ms delay):', {
          documentAnalysis: !!documentAnalysis,
          currentStep,
          selectedFile: !!selectedFile
        });
      }, 100);
      
      // NEW: Get intelligent category recommendations based on content signals
      let recommendations: CategoryRecommendation[] = [];
      try {
        const recommendationsResponse = await contentApi.recommendCategories(result.document_id);
        recommendations = recommendationsResponse.recommendations;
        setRecommendedCategories(recommendations);
        console.log(`📊 Received ${recommendationsResponse.total_recommended} category recommendations`);
      } catch (error) {
        console.error('Error fetching recommendations:', error);
        // Don't fail the whole flow if recommendations fail
      }
      
      console.log('📢 About to show success notification');
      console.log('🔍 State BEFORE notification:', {
        documentAnalysis: !!documentAnalysis,
        currentStep,
        selectedFile: !!selectedFile,
        hasStatistics: !!documentAnalysis?.statistics
      });
      addNotification('✅ Document analyzed successfully', 'success');
      console.log('✅ Success notification shown');
      
      // Check state immediately after notification (use requestAnimationFrame to check after React updates)
      requestAnimationFrame(() => {
        setTimeout(() => {
          console.log('🔍 State check AFTER notification (after React update):', {
            documentAnalysis: !!documentAnalysis,
            currentStep,
            selectedFile: !!selectedFile,
            hasStatistics: !!documentAnalysis?.statistics
          });
        }, 10);
      });
      
      // Load test types in background (for when user clicks "Select Test Types" button)
      // Don't navigate automatically - let user see results and click button
      console.log('🔄 Loading test types in background...');
      try {
        await loadAllTestTypes();
        console.log('✅ Test types loaded successfully');
        
        // Auto-select recommended categories after test types are loaded
        if (recommendations && recommendations.length > 0) {
          const recommendedIds = recommendations.map(rec => rec.category_id);
          setSelectedTestTypes(recommendedIds);
          console.log(`✨ Auto-selected ${recommendedIds.length} recommended categories`);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
        addNotification('Warning: Failed to load all test types. Some may not be available.', 'warning');
      }
      
      // Mark analysis as completed - user will click button to proceed
      analysisCompletedRef.current = true;
      analysisInProgressRef.current = false;
      console.log('✅ Analysis completed - waiting for user to click "Select Test Types" button');
    } catch (error: any) {
      console.error('Analysis error:', error);
      addNotification(error.response?.data?.detail || 'Failed to analyze document', 'error');
      analysisInProgressRef.current = false;
      analysisCompletedRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToSelection = async () => {
    // Ensure test types are loaded before navigating
    if (allTestTypes.length === 0) {
      console.log('🔄 Loading test types before proceeding...');
      await loadAllTestTypes();
    }
    
    // Auto-select recommended categories if available
    if (recommendedCategories && recommendedCategories.length > 0) {
      const recommendedIds = recommendedCategories.map(rec => rec.category_id);
      setSelectedTestTypes(recommendedIds);
      console.log(`✨ Auto-selected ${recommendedIds.length} recommended categories`);
    }
    
    // Navigate to selection step
    console.log('🎯 Navigating to selection step');
    setCurrentStep('selection');
  };

  const loadAllTestTypes = async () => {
    try {
      // Use new categories API instead of deprecated test-types API
      const response = await categoriesApi.list({ size: 100 });
      setAllTestTypes(response.categories.map(cat => ({
        id: cat.id,
        name: cat.name,
        category: cat.domain_name || '' // Use domain_name from Category
      })));
    } catch (error) {
      console.error('Error loading categories:', error);
      addNotification('Error loading categories', 'error');
    }
  };


  const toggleTechniqueSelection = (id: number) => {
    setSelectedTechniqueIds(prev => (
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    ));
  };

  const handleTestTypeToggle = (testTypeId: number) => {
    setSelectedTestTypes(prev => 
      prev.includes(testTypeId) 
        ? prev.filter(id => id !== testTypeId)
        : [...prev, testTypeId]
    );
  };

  // Load techniques for selected test types (categories)
  const loadTechniquesForSelectedTypes = useCallback(async (testTypeIds: number[]) => {
    if (testTypeIds.length === 0) {
      setAvailableTechniques([]);
      return;
    }

    try {
      setLoadingTechniques(true);
      // Use new techniques API - test type IDs are now category IDs
      // Support multiple category IDs by passing comma-separated string
      const categoryIds = testTypeIds.length === 1 
        ? testTypeIds[0].toString()
        : testTypeIds.join(',');
      
      const techniquesResponse = await techniquesApi.list({ 
        category_id: categoryIds,
        limit: 1000 
      });
      
      // Map techniques to match expected format
      const techniquesWithTypeInfo = techniquesResponse.techniques.map((technique: any) => {
        const testType = allTestTypes.find(tt => tt.id === technique.category_id);
        return {
          id: technique.id,
          name: technique.name,
          description: technique.description,
          test_type_id: technique.category_id, // Map category_id to test_type_id for compatibility
          test_type_name: testType?.name || technique.category_name || 'Unknown',
          test_type_category: testType?.category || technique.domain_name || 'Unknown'
        };
      });
      
      setAvailableTechniques(techniquesWithTypeInfo);
    } catch (error: any) {
      console.error('Error loading techniques:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to load test techniques';
      addNotification(`Error loading techniques: ${errorMessage}`, 'error');
    } finally {
      setLoadingTechniques(false);
    }
  }, [allTestTypes, addNotification]);

  // Load techniques when selected test types change
  useEffect(() => {
    if (selectedTestTypes.length > 0 && allTestTypes.length > 0) {
      loadTechniquesForSelectedTypes(selectedTestTypes);
    }
  }, [selectedTestTypes, allTestTypes, loadTechniquesForSelectedTypes]);

  // Group test types by category
  const groupedTestTypes = allTestTypes.reduce((groups, testType) => {
    const category = testType.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(testType);
    return groups;
  }, {} as Record<string, Array<{id: number, name: string, category: string}>>);

  // Get category display name
  const getCategoryDisplayName = (category: string) => {
    return category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get category color classes
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'KNOWLEDGE_BASE':
        return 'bg-blue-100 text-blue-800';
      case 'RAG_BASED':
        return 'bg-green-100 text-green-800';
      case 'ADVERSARIAL_BASED':
        return 'bg-red-100 text-red-800';
      case 'AGENT_BASED':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-slate-600 text-slate-200';
    }
  };

  // Handle select all for a category
  const handleSelectAllForCategory = (category: string) => {
    const categoryTestTypes = groupedTestTypes[category] || [];
    const categoryIds = categoryTestTypes.map(tt => tt.id);
    const allSelected = categoryIds.every(id => selectedTestTypes.includes(id));
    
    if (allSelected) {
      // Deselect all in this category
      setSelectedTestTypes(prev => prev.filter(id => !categoryIds.includes(id)));
    } else {
      // Select all in this category
      setSelectedTestTypes(prev => {
        const combined = [...prev, ...categoryIds];
        return Array.from(new Set(combined));
      });
    }
  };

  // Check if all test types in a category are selected
  const isCategoryFullySelected = (category: string) => {
    const categoryTestTypes = groupedTestTypes[category] || [];
    return categoryTestTypes.length > 0 && categoryTestTypes.every(tt => selectedTestTypes.includes(tt.id));
  };

  // Check if some test types in a category are selected
  const isCategoryPartiallySelected = (category: string) => {
    const categoryTestTypes = groupedTestTypes[category] || [];
    return categoryTestTypes.some(tt => selectedTestTypes.includes(tt.id)) && !isCategoryFullySelected(category);
  };

  // Handle test case generation
  const handleGenerateTestCases = async () => {
    if (!selectedFile || selectedTestTypes.length === 0) {
      addNotification('Please select a file and test types', 'error');
      return;
    }

    try {
      setLoading(true);
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const organizationId = orgIdStr ? parseInt(orgIdStr, 10) : 1;
      const projectId = projectIdStr ? parseInt(projectIdStr, 10) : 1;
      const result = await testCasesApi.generateWithTestTypes(
        selectedFile,
        organizationId,
        projectId,
        selectedTestTypes,
        selectedTechniqueIds.length > 0 ? selectedTechniqueIds : undefined,
        numTestCases
      );

      const generatedTestCases: GeneratedTestCase[] = (result.test_cases || []).map((testCase: any) => ({
        question: testCase.question,
        test_type: testCase.test_type || testCase.category || 'General',
        test_type_id: testCase.test_type_id ?? testCase.category_id,
        test_technique: testCase.test_technique || testCase.technique || 'General',
        test_technique_id: testCase.test_technique_id ?? testCase.technique_id
      }));
      
      setGeneratedTestCases(generatedTestCases);
      setTechniquesUsed((result.techniques_used || []).map((technique: any) => ({
        id: technique.id,
        name: technique.name,
        test_type: technique.test_type || technique.category || 'General',
        test_type_id: technique.test_type_id ?? technique.category_id
      })));
      
      // Initialize all test cases as PENDING
      const initialStatuses: Record<number, 'APPROVED' | 'REJECTED' | 'PENDING'> = {};
      generatedTestCases.forEach((_: GeneratedTestCase, index: number) => {
        initialStatuses[index] = 'PENDING';
      });
      setTestCaseStatuses(initialStatuses);
      
      addNotification(`Successfully generated ${generatedTestCases.length} test cases`, 'success');
    } catch (error: any) {
      console.error('Error generating test cases:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to generate test cases';
      addNotification(`Error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle test case approval
  const handleApproveTestCase = async (testCaseIndex: number) => {
    try {
      setApprovingTestCases(prev => new Set(prev).add(testCaseIndex));
      
      // Get the test case data
      const testCase = generatedTestCases[testCaseIndex];
      if (!testCase) {
        throw new Error('Test case not found');
      }

      // Create the test case in the database first
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const createdTestCase = await testCasesApi.create({
        question_text: testCase.question,
        source_type: 'pdf', // Since we're generating from PDF
        source_file: selectedFile?.name || 'generated',
        source_content: documentAnalysis?.summary || '',
        category: buildGeneratedLabel(testCase),
        technique_id: testCase.test_technique_id,
        status: 'APPROVED', // Create as approved directly
        organization_id: orgIdStr ? parseInt(orgIdStr) : undefined,
        project_id: projectIdStr ? parseInt(projectIdStr) : undefined
      });

      // Update local status
      setTestCaseStatuses(prev => ({
        ...prev,
        [testCaseIndex]: 'APPROVED'
      }));
      
      addNotification('Test case approved and saved successfully', 'success');
    } catch (error: any) {
      console.error('Error approving test case:', error);
      // Revert status on error
      setTestCaseStatuses(prev => ({
        ...prev,
        [testCaseIndex]: 'PENDING'
      }));
      addNotification('Failed to approve test case', 'error');
    } finally {
      setApprovingTestCases(prev => {
        const newSet = new Set(prev);
        newSet.delete(testCaseIndex);
        return newSet;
      });
    }
  };

  // Handle test case rejection
  const handleRejectTestCase = async (testCaseIndex: number) => {
    try {
      setApprovingTestCases(prev => new Set(prev).add(testCaseIndex));
      
      // Get the test case data
      const testCase = generatedTestCases[testCaseIndex];
      if (!testCase) {
        throw new Error('Test case not found');
      }

      // Create the test case in the database as rejected
      const orgIdStr2 = localStorage.getItem('selectedOrganizationId');
      const projectIdStr2 = localStorage.getItem('selectedProjectId');
      const createdTestCase = await testCasesApi.create({
        question_text: testCase.question,
        source_type: 'pdf', // Since we're generating from PDF
        source_file: selectedFile?.name || 'generated',
        source_content: documentAnalysis?.summary || '',
        category: buildGeneratedLabel(testCase),
        technique_id: testCase.test_technique_id,
        status: 'REJECTED', // Create as rejected directly
        organization_id: orgIdStr2 ? parseInt(orgIdStr2) : undefined,
        project_id: projectIdStr2 ? parseInt(projectIdStr2) : undefined
      });

      // Update local status
      setTestCaseStatuses(prev => ({
        ...prev,
        [testCaseIndex]: 'REJECTED'
      }));
      
      addNotification('Test case rejected and saved successfully', 'success');
    } catch (error: any) {
      console.error('Error rejecting test case:', error);
      // Revert status on error
      setTestCaseStatuses(prev => ({
        ...prev,
        [testCaseIndex]: 'PENDING'
      }));
      addNotification('Failed to reject test case', 'error');
    } finally {
      setApprovingTestCases(prev => {
        const newSet = new Set(prev);
        newSet.delete(testCaseIndex);
        return newSet;
      });
    }
  };

  // Get status badge component
  const getStatusBadge = (status: 'APPROVED' | 'REJECTED' | 'PENDING') => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'APPROVED':
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            <Check className="h-3 w-3 mr-1" />
            Approved
          </span>
        );
      case 'REJECTED':
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </span>
        );
      case 'PENDING':
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </span>
        );
      default:
        return null;
    }
  };

  // Filter techniques based on search and type filter
  const filteredTechniques = availableTechniques.filter(technique => {
    const matchesSearch = techniqueSearchTerm === '' || 
      technique.name.toLowerCase().includes(techniqueSearchTerm.toLowerCase()) ||
      (technique.description && technique.description.toLowerCase().includes(techniqueSearchTerm.toLowerCase()));
    
    const matchesType = techniqueFilterType === 'all' || technique.test_type_name === techniqueFilterType;
    
    return matchesSearch && matchesType;
  });

  // Get unique test type names for filter dropdown
  const uniqueTestTypeNames = Array.from(new Set(availableTechniques.map(t => t.test_type_name))).sort();

  // Filter and group generated test cases
  const filteredTestCases = generatedTestCases.filter((testCase, index) => {
    const matchesTechnique = resultFilterTechnique === 'all' || testCase.test_technique === resultFilterTechnique;
    const matchesTestType = resultFilterTestType === 'all' || testCase.test_type === resultFilterTestType;
    const status = testCaseStatuses[index] || 'PENDING';
    const matchesStatus = resultFilterStatus === 'all' || status === resultFilterStatus;
    return matchesTechnique && matchesTestType && matchesStatus;
  });

  // Group test cases based on selected grouping option
  const groupedTestCases = resultGroupBy === 'none' ? 
    { 'All Test Cases': filteredTestCases } :
    resultGroupBy === 'technique' ?
    filteredTestCases.reduce((groups, testCase) => {
      const technique = testCase.test_technique;
      if (!groups[technique]) {
        groups[technique] = [];
      }
      groups[technique].push(testCase);
      return groups;
    }, {} as Record<string, GeneratedTestCase[]>) :
    filteredTestCases.reduce((groups, testCase) => {
      const testType = testCase.test_type;
      if (!groups[testType]) {
        groups[testType] = [];
      }
      groups[testType].push(testCase);
      return groups;
    }, {} as Record<string, GeneratedTestCase[]>);

  // Get unique values for filter dropdowns
  const uniqueTechniques = Array.from(new Set(generatedTestCases.map(tc => tc.test_technique))).sort();
  const uniqueTestTypes = Array.from(new Set(generatedTestCases.map(tc => tc.test_type))).sort();

  const resetFlow = () => {
    setCurrentStep('upload');
    setSelectedFile(null);
    setDocumentAnalysis(null);
    setSelectedTestTypes([]);
    setAllTestTypes([]);
    setGeneratedTestCases([]);
    setTechniquesUsed([]);
    setAvailableTechniques([]);
    setSelectedTechniqueForDetails(null);
    setTechniqueSearchTerm('');
    setTechniqueFilterType('all');
    setResultGroupBy('none');
    setResultFilterTechnique('all');
    setResultFilterTestType('all');
    setResultFilterStatus('all');
    setTestCaseStatuses({});
    setApprovingTestCases(new Set());
  };

  const renderStepIndicator = () => {
    // Get step 1 name based on input method
    const getStep1Name = () => {
      if (currentStep === 'upload') {
        switch (activeTab) {
          case 'pdf': return 'Upload & Analyze';
          case 'webpage': return 'Enter Webpage';
          case 'manual': return 'Enter Content';
          default: return 'Upload & Analyze';
        }
      } else {
        // After upload, show the method that was used
        switch (activeTab) {
          case 'pdf': return 'PDF Uploaded';
          case 'webpage': return 'Webpage Entered';
          case 'manual': return 'Content Entered';
          default: return 'Upload & Analyze';
        }
      }
    };

    return (
      <div className="flex items-center justify-center mb-8">
        <div className="flex items-center space-x-4">
          <div className={`flex items-center space-x-2 px-4 py-2 rounded-full ${
            currentStep === 'upload' ? 'bg-indigo-100 text-indigo-700' : 
            currentStep === 'selection' || currentStep === 'generation' ? 'bg-green-100 text-green-700' : 
            'bg-gray-100 text-slate-400'
          }`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
              currentStep === 'upload' ? 'bg-indigo-600 text-white' : 
              currentStep === 'selection' || currentStep === 'generation' ? 'bg-green-600 text-white' : 
              'bg-gray-400 text-white'
            }`}>
              1
            </div>
            <span className="font-medium">{getStep1Name()}</span>
          </div>
          
          <ArrowRight className="h-4 w-4 text-gray-400" />
          
          <div className={`flex items-center space-x-2 px-4 py-2 rounded-full ${
            currentStep === 'selection' ? 'bg-indigo-100 text-indigo-700' : 
            currentStep === 'generation' ? 'bg-green-100 text-green-700' : 
            'bg-gray-100 text-slate-400'
          }`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
              currentStep === 'selection' ? 'bg-indigo-600 text-white' : 
              currentStep === 'generation' ? 'bg-green-600 text-white' : 
              'bg-gray-400 text-white'
            }`}>
              2
            </div>
            <span className="font-medium">Select Test Types</span>
          </div>
          
          <ArrowRight className="h-4 w-4 text-gray-400" />
          
          <div className={`flex items-center space-x-2 px-4 py-2 rounded-full ${
            currentStep === 'generation' ? 'bg-indigo-100 text-indigo-700' : 
            'bg-gray-100 text-slate-400'
          }`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
              currentStep === 'generation' ? 'bg-indigo-600 text-white' : 
              'bg-gray-400 text-white'
            }`}>
              3
            </div>
            <span className="font-medium">Generate Test Cases</span>
          </div>
        </div>
      </div>
    );
  };

  const renderPDFUpload = () => (
    <div className="space-y-6">
      <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-slate-500 transition-colors">
        <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <h3 className="text-lg font-medium text-slate-100 mb-2">Upload PDF Document</h3>
        <p className="text-slate-400 mb-4">
          Drag and drop your PDF file here, or click to browse
        </p>
        <input
          type="file"
          accept=".pdf"
          className="hidden"
          id="pdf-upload"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              // Only allow file selection if we're not in the middle of analysis flow
              if (!analysisInProgressRef.current && !analysisCompletedRef.current) {
                handleFileSelect(file);
              } else {
                console.log('⚠️ Ignoring file select - analysis in progress or completed');
                // Reset the input
                e.target.value = '';
              }
            }
          }}
        />
        <label
          htmlFor="pdf-upload"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 cursor-pointer"
        >
          Choose PDF File
        </label>
      </div>

      {selectedFile && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
            <div>
              <p className="text-sm font-medium text-green-800">File Selected</p>
              <p className="text-sm text-green-600">{selectedFile.name}</p>
            </div>
          </div>
        </div>
      )}

      {selectedFile && (
        <button
          type="button"
          onClick={handleAnalyzeDocument}
          disabled={loading}
          className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader className="animate-spin h-5 w-5 mr-2" />
              Analyzing Document...
            </>
          ) : (
            <>
              <Search className="h-5 w-5 mr-2" />
              Analyze Document
            </>
          )}
        </button>
      )}

      {/* Analysis Results Card */}
      {documentAnalysis && currentStep === 'upload' && (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center">
            <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
            Document Analysis Complete
          </h3>
          
          {documentAnalysis.statistics && (
            <div className="space-y-4">
              {/* Processing Summary */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-slate-200">Processing Summary</h4>
                  <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                    {documentAnalysis.processing_time_ms}ms
                  </span>
                </div>
                <p className="text-sm text-slate-200">{documentAnalysis.reasoning}</p>
              </div>

              {/* Statistics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-900">
                    {documentAnalysis.statistics.sections}
                  </div>
                  <div className="text-sm text-blue-700 mt-1">Sections</div>
                  <div className="text-xs text-blue-600 mt-1">Document structure</div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-purple-900">
                    {documentAnalysis.statistics.sentences}
                  </div>
                  <div className="text-sm text-purple-700 mt-1">Sentences</div>
                  <div className="text-xs text-purple-600 mt-1">Content units</div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-900">
                    {documentAnalysis.statistics.entities}
                  </div>
                  <div className="text-sm text-green-700 mt-1">Entities</div>
                  <div className="text-xs text-green-600 mt-1">Key information</div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-yellow-900">
                    {documentAnalysis.statistics.tables}
                  </div>
                  <div className="text-sm text-yellow-700 mt-1">Tables</div>
                  <div className="text-xs text-yellow-600 mt-1">Structured data</div>
                </div>

                <div className="bg-indigo-50 border border-purple-500 rounded-lg p-4">
                  <div className="text-2xl font-bold text-indigo-900">
                    {documentAnalysis.statistics.references}
                  </div>
                  <div className="text-sm text-indigo-700 mt-1">References</div>
                  <div className="text-xs text-indigo-600 mt-1">Cross-references</div>
                </div>

                <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-pink-900">
                    {documentAnalysis.statistics.topics}
                  </div>
                  <div className="text-sm text-pink-700 mt-1">Topics</div>
                  <div className="text-xs text-pink-600 mt-1">Key themes</div>
                </div>
              </div>

              {/* Information Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-800">
                      <strong>Document ID:</strong> {documentAnalysis.document_id}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      The document has been processed and analyzed. You can now proceed to select test types for test case generation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleProceedToSelection}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              Next: Select Test Types
              <ArrowRight className="h-4 w-4 ml-2" />
            </button>
          </div>
        </div>
      )}
    </div>
  );


  const renderSelectionStep = () => {
    console.log('🎨 renderSelectionStep called');
    console.log('🔍 allTestTypes length:', allTestTypes.length);
    console.log('🔍 selectedTestTypes:', selectedTestTypes);
    console.log('🔍 recommendedCategories length:', recommendedCategories.length);
    
    return (
    <div className="space-y-6">
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">Select Test Types</h3>
        <p className="text-sm text-slate-300 mb-4">
          Choose the test types you want to use for generating test cases. Test types are grouped by category for easy selection.
        </p>

        {/* Intelligent Recommendations Summary */}
        {recommendedCategories.length > 0 && (
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              </div>
              <div className="ml-3 flex-1">
                <h4 className="text-sm font-medium text-green-900 mb-2">
                  ✨ {recommendedCategories.length} Test {recommendedCategories.length === 1 ? 'Category' : 'Categories'} Recommended
                </h4>
                <p className="text-xs text-green-700 mb-3">
                  Based on your document's content signals, we've automatically selected the most relevant test categories for you. You can modify the selection below.
                </p>
                <div className="flex flex-wrap gap-2">
                  {recommendedCategories.map((rec) => (
                    <div key={rec.category_id} className="inline-flex items-center space-x-1 px-2 py-1 rounded-full bg-slate-700 border border-green-500 text-xs">
                      <span className="font-medium text-green-800">{rec.category_name}</span>
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                        rec.confidence === 'high' ? 'bg-green-100 text-green-700' :
                        rec.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-slate-600 text-slate-200'
                      }`}>
                        {Math.round(rec.score * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {Object.entries(groupedTestTypes).map(([category, testTypes]) => (
            <div key={category} className="border border-slate-700 rounded-lg p-4">
              {/* Category Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <h4 className="text-lg font-medium text-slate-100">
                    {getCategoryDisplayName(category)}
                  </h4>
                  <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(category)}`}>
                    {testTypes.length} test type{testTypes.length !== 1 ? 's' : ''}
                  </span>
                  {(() => {
                    const recommendedInCategory = testTypes.filter(tt => 
                      recommendedCategories.some(rec => rec.category_id === tt.id)
                    ).length;
                    return recommendedInCategory > 0 ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 flex items-center">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {recommendedInCategory} recommended
                      </span>
                    ) : null;
                  })()}
                </div>
                
                {/* Select All / Deselect All Button */}
                <button
                  onClick={() => handleSelectAllForCategory(category)}
                  className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    isCategoryFullySelected(category)
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : isCategoryPartiallySelected(category)
                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                      : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                  }`}
                >
                  {isCategoryFullySelected(category) ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              {/* Test Types Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {testTypes.map((testType) => {
                  // NEW: Check if this category is recommended by intelligent system
                  const recommendation = recommendedCategories.find(rec => rec.category_id === testType.id);
                  const isRecommended = !!recommendation;
                  
                  return (
                    <div
                      key={testType.id}
                      className={`p-3 border rounded-lg transition-colors ${
                        selectedTestTypes.includes(testType.id)
                          ? 'border-indigo-500 bg-indigo-50'
                          : isRecommended
                          ? 'border-green-300 bg-green-50 hover:border-green-400 ring-2 ring-green-200'
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          checked={selectedTestTypes.includes(testType.id)}
                          onChange={() => handleTestTypeToggle(testType.id)}
                          className="mt-1 h-4 w-4 text-purple-500 focus:ring-purple-500 border-slate-600 rounded cursor-pointer"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <h5 className="text-sm font-medium text-slate-100">{testType.name}</h5>
                            {isRecommended && (
                              <div className="flex items-center space-x-1">
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  recommendation.confidence === 'high' ? 'bg-green-100 text-green-800' :
                                  recommendation.confidence === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-slate-600 text-slate-200'
                                }`}>
                                  ✨ {recommendation.confidence === 'high' ? 'Highly' : recommendation.confidence === 'medium' ? 'Moderately' : ''} Recommended
                                </span>
                                <span className="text-xs text-slate-400" title={`Score: ${recommendation.score.toFixed(2)}`}>
                                  {Math.round(recommendation.score * 100)}%
                                </span>
                              </div>
                            )}
                          </div>
                          {isRecommended && recommendation.matched_signals.length > 0 && (
                            <div className="mt-2 text-xs text-slate-300">
                              <span className="font-medium">Matched signals:</span> {recommendation.matched_signals.map(s => `${s.signal_name} (${s.signal_count})`).join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Technique Preview Section */}
        {selectedTestTypes.length > 0 && (
          <div className="mt-6 border-t border-slate-700 pt-6 technique-preview-mobile">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-slate-100">Available Test Techniques</h4>
              {loadingTechniques && (
                <div className="flex items-center text-sm text-slate-400">
                  <Loader className="animate-spin h-4 w-4 mr-2" />
                  <span className="animate-pulse">Loading techniques...</span>
                </div>
              )}
            </div>

            {/* Search and Filter Controls */}
            {availableTechniques.length > 0 && !loadingTechniques && (
              <div className="mb-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search techniques..."
                      value={techniqueSearchTerm}
                      onChange={(e) => setTechniqueSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-sm"
                    />
                  </div>

                  {/* Test Type Filter */}
                  <div>
                    <select
                      value={techniqueFilterType}
                      onChange={(e) => setTechniqueFilterType(e.target.value as 'all' | string)}
                      className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-sm"
                    >
                      <option value="all">All Test Types</option>
                      {uniqueTestTypeNames.map((typeName) => (
                        <option key={typeName} value={typeName}>
                          {typeName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Results Count */}
                <div className="text-sm text-slate-300">
                  Showing {filteredTechniques.length} of {availableTechniques.length} techniques
                </div>
              </div>
            )}
            
            {loadingTechniques ? (
              <div className="space-y-4">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="space-y-3">
                    <div className="bg-slate-700/50 border border-slate-700 rounded-lg p-4">
                      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="flex space-x-2">
                        <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                        <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                      </div>
                    </div>
                    <div className="bg-slate-700/50 border border-slate-700 rounded-lg p-4">
                      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="flex space-x-2">
                        <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                        <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : availableTechniques.length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-slate-300">
                  Select the test techniques to use for generation. If none selected, all will be used.
                </p>
                
                {/* Group techniques by test type */}
                {Object.entries(
                  filteredTechniques.reduce((groups, technique) => {
                    const testTypeName = technique.test_type_name;
                    if (!groups[testTypeName]) {
                      groups[testTypeName] = [];
                    }
                    groups[testTypeName].push(technique);
                    return groups;
                  }, {} as Record<string, typeof filteredTechniques>)
                ).map(([testTypeName, techniques]) => (
                  <div key={testTypeName} className="bg-slate-700/50 border border-slate-700 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-slate-100 mb-3 flex items-center">
                      <Tag className="h-4 w-4 mr-2" />
                      {testTypeName}
                      <span className="ml-2 text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                        {techniques.length} technique{techniques.length !== 1 ? 's' : ''}
                      </span>
                    </h5>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 technique-grid">
                      {techniques.map((technique) => (
                        <div key={technique.id} className="bg-slate-800 border border-slate-700 rounded-lg p-4 hover:border-purple-500 hover:shadow-sm transition-all duration-200 technique-card-mobile">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <div className="font-medium text-slate-100 text-sm technique-name">{technique.name}</div>
                                <button
                                  onClick={() => setSelectedTechniqueForDetails(technique)}
                                  className="text-gray-400 hover:text-indigo-600 transition-colors"
                                  title="View details"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              </div>
                              {technique.description && (
                                <div className="text-xs text-slate-300 mt-1 line-clamp-2 technique-description">
                                  {technique.description}
                                </div>
                              )}
                              <div className="flex items-center space-x-2 mt-2">
                                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                                  {technique.test_type_name}
                                </span>
                                <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                                  Approved
                                </span>
                                <label className="ml-auto inline-flex items-center space-x-2 text-xs text-slate-200">
                                  <input
                                    type="checkbox"
                                    checked={selectedTechniqueIds.includes(technique.id)}
                                    onChange={() => toggleTechniqueSelection(technique.id)}
                                    className="h-4 w-4 text-purple-500 focus:ring-purple-500 border-slate-600 rounded"
                                  />
                                  <span>Select</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-800">
                        <strong>Selected techniques:</strong> {selectedTechniqueIds.length > 0 ? selectedTechniqueIds.length : filteredTechniques.length}
                        {selectedTechniqueIds.length === 0 && ' (all)'}
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        Each selected technique will generate test cases based on its specific methodology and prompts.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : filteredTechniques.length === 0 && availableTechniques.length > 0 ? (
              <div className="bg-slate-700/50 border border-slate-700 rounded-lg p-6 text-center">
                <Search className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-slate-300">
                  No techniques match your search criteria.
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Try adjusting your search term or filter to see more results.
                </p>
              </div>
            ) : !loadingTechniques ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-yellow-600"></div>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-800">
                      <strong>No approved techniques found</strong> for the selected test types.
                    </p>
                    <p className="text-xs text-yellow-600 mt-1">
                      To proceed with test case generation, you need at least one approved test technique for each selected test type.
                    </p>
                    <div className="mt-2 text-xs text-yellow-700">
                      <strong>Next steps:</strong>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>Go to <strong>Test Techniques</strong> page to create and approve techniques</li>
                        <li>Or select different test types that have approved techniques</li>
                        <li>Contact your administrator if you need help</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setCurrentStep('upload')}
            className="inline-flex items-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous Step
          </button>

          <div className="flex items-center space-x-4">
            <div className="text-sm text-slate-300">
              Number of test cases:
            </div>
            <input
              type="number"
              min="1"
              max={maxTestCasesPerGeneration}
              value={numTestCases}
              onChange={(e) => setNumTestCases(Math.min(parseInt(e.target.value) || 5, maxTestCasesPerGeneration))}
              className="w-20 px-3 py-1 border border-slate-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <button
            onClick={() => setCurrentStep('generation')}
            disabled={selectedTestTypes.length === 0 || (filteredTechniques.length === 0 && !loadingTechniques)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue to Generation
            <ArrowRight className="h-4 w-4 ml-2" />
          </button>
        </div>
      </div>
    </div>
    );
  };

  const renderGenerationStep = () => (
    <div className="space-y-6">
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h4 className="text-lg font-medium text-slate-100 mb-4">Generate Test Cases</h4>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-200 mb-2">
            Number of Test Cases
          </label>
          <input
            type="number"
            min="1"
            max="20"
            value={numTestCases}
            onChange={(e) => setNumTestCases(parseInt(e.target.value) || 5)}
            className="w-32 px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>

        <div className="mb-6">
          <h5 className="text-sm font-medium text-slate-200 mb-3">Selected Test Types</h5>
          <div className="flex flex-wrap gap-2">
            {documentAnalysis?.applicable_test_types
              .filter(tt => selectedTestTypes.includes(tt.id))
              .map((testType) => (
                <span key={testType.id} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  <Tag className="h-3 w-3 mr-1" />
                  {testType.name}
                </span>
              ))}
          </div>
        </div>

        {techniquesUsed.length > 0 && (
          <div className="mb-6">
            <h5 className="text-sm font-medium text-slate-200 mb-3">Test Techniques Being Used</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {techniquesUsed.map((technique) => (
                <div key={technique.id} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-blue-900 text-sm">{technique.name}</div>
                      <div className="text-xs text-blue-700 mt-1">{technique.test_type}</div>
                    </div>
                    <div className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                      Technique
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-2">
              These techniques will be used to generate test cases based on their specific methodologies and prompts.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentStep('upload')}
            className="inline-flex items-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous Step
          </button>
          
          <button
            onClick={handleGenerateTestCases}
            disabled={loading || selectedTestTypes.length === 0}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader className="animate-spin h-5 w-5 mr-2" />
                Generating Test Cases...
              </>
            ) : (
              <>
                <FileText className="h-5 w-5 mr-2" />
                Generate Test Cases
              </>
            )}
          </button>
        </div>
      </div>

      {generatedTestCases.length > 0 && (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-slate-100">Generated Test Cases</h4>
            <span className="text-sm text-slate-400">{filteredTestCases.length} of {generatedTestCases.length} test cases</span>
          </div>

          {/* Results Filtering and Grouping Controls */}
          <div className="mb-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 filter-controls-mobile">
              {/* Group By */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">Group By</label>
                <select
                  value={resultGroupBy}
                  onChange={(e) => setResultGroupBy(e.target.value as 'none' | 'technique' | 'test_type')}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-sm"
                >
                  <option value="none">No Grouping</option>
                  <option value="technique">By Technique</option>
                  <option value="test_type">By Test Type</option>
                </select>
              </div>

              {/* Filter by Technique */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">Filter by Technique</label>
                <select
                  value={resultFilterTechnique}
                  onChange={(e) => setResultFilterTechnique(e.target.value as 'all' | string)}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-sm"
                >
                  <option value="all">All Techniques</option>
                  {uniqueTechniques.map((technique) => (
                    <option key={technique} value={technique}>
                      {technique}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filter by Test Type */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">Filter by Test Type</label>
                <select
                  value={resultFilterTestType}
                  onChange={(e) => setResultFilterTestType(e.target.value as 'all' | string)}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-sm"
                >
                  <option value="all">All Test Types</option>
                  {uniqueTestTypes.map((testType) => (
                    <option key={testType} value={testType}>
                      {testType}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filter by Status */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">Filter by Status</label>
                <select
                  value={resultFilterStatus}
                  onChange={(e) => setResultFilterStatus(e.target.value as 'all' | 'APPROVED' | 'REJECTED' | 'PENDING')}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-sm"
                >
                  <option value="all">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
            </div>

            {/* Results Summary */}
            <div className="bg-slate-700/50 border border-slate-700 rounded-lg p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-300">
                  Showing <strong>{filteredTestCases.length}</strong> test cases
                  {resultGroupBy !== 'none' && (
                    <span> grouped by <strong>{resultGroupBy === 'technique' ? 'Technique' : 'Test Type'}</strong></span>
                  )}
                </span>
                <div className="flex items-center space-x-4 text-xs text-slate-400">
                  <span>Techniques: {uniqueTechniques.length}</span>
                  <span>Test Types: {uniqueTestTypes.length}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Technique Statistics */}
          <div className="mb-6 bg-gradient-to-r from-indigo-50 to-blue-50 border border-purple-500 rounded-lg p-4">
            <h5 className="text-sm font-medium text-indigo-900 mb-3">Technique Usage Statistics</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stats-mobile">
              {uniqueTechniques.map((technique) => {
                const count = filteredTestCases.filter(tc => tc.test_technique === technique).length;
                const percentage = filteredTestCases.length > 0 ? Math.round((count / filteredTestCases.length) * 100) : 0;
                return (
                  <div key={technique} className="bg-slate-800 border border-purple-500 rounded-lg p-3 stat-card">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-100 truncate">{technique}</span>
                      <span className="text-xs text-indigo-600 font-medium">{percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-indigo-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">{count} test case{count !== 1 ? 's' : ''}</div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="space-y-6">
            {Object.entries(groupedTestCases).map(([groupName, testCases]) => (
              <div key={groupName} className="results-group-mobile">
                {resultGroupBy !== 'none' && (
                  <div className="mb-4">
                    <h5 className="text-lg font-medium text-slate-100 flex items-center group-header">
                      <Tag className="h-5 w-5 mr-2" />
                      {groupName}
                      <span className="ml-2 text-sm px-2 py-1 rounded-full bg-indigo-100 text-indigo-800">
                        {testCases.length} test case{testCases.length !== 1 ? 's' : ''}
                      </span>
                    </h5>
                  </div>
                )}
                
                <div className="space-y-3">
                  {testCases.map((testCase, index) => {
                    const globalIndex = generatedTestCases.findIndex(tc => tc === testCase);
                    const status = testCaseStatuses[globalIndex] || 'PENDING';
                    const isApproving = approvingTestCases.has(globalIndex);
                    
                    return (
                      <div key={`${groupName}-${index}`} className="border border-slate-700 rounded-lg p-4 hover:border-purple-500 hover:shadow-sm transition-all duration-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-slate-100 mb-3 leading-relaxed">{testCase.question}</p>
                            <div className="flex items-center space-x-2 mb-3">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                <Tag className="h-3 w-3 mr-1" />
                                {testCase.test_type}
                              </span>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {testCase.test_technique}
                              </span>
                              {getStatusBadge(status)}
                            </div>
                          </div>
                          
                          {/* Approval Controls */}
                          <div className="flex items-center space-x-2 ml-4">
                            {status === 'PENDING' && (
                              <>
                                <button
                                  onClick={() => handleApproveTestCase(globalIndex)}
                                  disabled={isApproving}
                                  className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isApproving ? (
                                    <Loader className="animate-spin h-3 w-3 mr-1" />
                                  ) : (
                                    <Check className="h-3 w-3 mr-1" />
                                  )}
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleRejectTestCase(globalIndex)}
                                  disabled={isApproving}
                                  className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isApproving ? (
                                    <Loader className="animate-spin h-3 w-3 mr-1" />
                                  ) : (
                                    <XCircle className="h-3 w-3 mr-1" />
                                  )}
                                  Reject
                                </button>
                              </>
                            )}
                            {(status === 'APPROVED' || status === 'REJECTED') && (
                              <button
                                onClick={() => handleApproveTestCase(globalIndex)}
                                disabled={isApproving}
                                className="inline-flex items-center px-3 py-1 border border-slate-600 text-xs font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isApproving ? (
                                  <Loader className="animate-spin h-3 w-3 mr-1" />
                                ) : (
                                  <Check className="h-3 w-3 mr-1" />
                                )}
                                Approve
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            
            {filteredTestCases.length === 0 && generatedTestCases.length > 0 && (
              <div className="bg-slate-700/50 border border-slate-700 rounded-lg p-6 text-center">
                <Search className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-slate-300">
                  No test cases match your filter criteria.
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Try adjusting your filters to see more results.
                </p>
              </div>
            )}
          </div>
          
          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={() => setCurrentStep('upload')}
              className="inline-flex items-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Previous Step
            </button>
            
            <button
              onClick={resetFlow}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Generate More
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderWebpageContent = () => (
    <div className="space-y-6">
      <div className="text-center py-12">
        <Globe className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p className="text-slate-400 text-lg">Webpage analysis coming soon</p>
        <p className="text-gray-400 text-sm">This feature will be available in a future update</p>
      </div>
    </div>
  );

  const renderManualInput = () => (
    <div className="space-y-6">
      <div className="text-center py-12">
        <Plus className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p className="text-slate-400 text-lg">Manual input coming soon</p>
        <p className="text-gray-400 text-sm">This feature will be available in a future update</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Test Case Generation</h1>
            <p className="text-slate-300 mt-2">
              Generate test cases from documents using AI-powered analysis and Knowledge Base test types
            </p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-md">
            <CheckCircle className="h-4 w-4" />
            <span>Enhanced with Document Analysis</span>
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      {renderStepIndicator()}

      {/* Generation Methods */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700">
        {/* Tabs */}
        {/* Tab Navigation - Only show on upload step */}
        {currentStep === 'upload' && (
          <div className="border-b border-slate-700">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {[
                { id: 'pdf', name: 'PDF Upload', icon: Upload },
                { id: 'webpage', name: 'Webpage Content', icon: Globe },
                { id: 'manual', name: 'Manual Input', icon: Plus }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id as any)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-500'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        {/* Tab Content */}
        <div className="p-6">
          {currentStep === 'upload' ? (
            // Show tab-specific content only on upload step
            <>
              {activeTab === 'pdf' && renderPDFUpload()}
              {activeTab === 'webpage' && renderWebpageContent()}
              {activeTab === 'manual' && renderManualInput()}
            </>
          ) : (
            // Unified flow for selection and generation steps
            <>
              {(() => {
                console.log('🔍 Rendering step content, currentStep:', currentStep);
                if (currentStep === 'selection') {
                  console.log('✅ Rendering selection step');
                  return renderSelectionStep();
                } else if (currentStep === 'generation') {
                  console.log('✅ Rendering generation step');
                  return renderGenerationStep();
                } else {
                  console.log('⚠️ Unknown step:', currentStep);
                  return null;
                }
              })()}
            </>
          )}
        </div>
      </div>

      {/* Technique Details Modal */}
      {selectedTechniqueForDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-slate-800 modal-mobile">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-slate-100">Technique Details</h3>
              <button
                onClick={() => setSelectedTechniqueForDetails(null)}
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              {selectedTechniqueForDetails && (
                <>
                  <div>
                    <h4 className="text-sm font-medium text-slate-200 mb-2">Name</h4>
                    <p className="text-slate-100">{selectedTechniqueForDetails.name}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-slate-200 mb-2">Test Type</h4>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                        {selectedTechniqueForDetails.test_type_name}
                      </span>
                      <span className="text-xs text-slate-400">
                        ({selectedTechniqueForDetails.test_type_category.replace('_', ' ')})
                      </span>
                    </div>
                  </div>
                  
                  {selectedTechniqueForDetails.description && (
                    <div>
                      <h4 className="text-sm font-medium text-slate-200 mb-2">Description</h4>
                      <p className="text-slate-100 text-sm leading-relaxed">
                        {selectedTechniqueForDetails.description}
                      </p>
                    </div>
                  )}
                </>
              )}
              
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-slate-200">Status:</span>
                <span className="text-sm px-2 py-1 rounded-full bg-green-100 text-green-800">
                  Approved
                </span>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedTechniqueForDetails(null)}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestCaseGeneration;